<template>
    <div class="space-y-6">
        <div class="space-y-1">
            <div class="flex h-8 items-center">
                <h4 class="text-sm font-medium text-gray-600">Open</h4>
            </div>
            <UTable
                :rows="open"
                :columns="columns"
                :loading="loading"
                :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                :empty-state="{ icon: 'i-ph-clipboard-text-duotone', label: 'No open interventions.' }"
                :ui="tableUi"
                @select="openIntervention"
            >
                <template #number-data="{ row }">
                    <ULink :to="interventionPath(row)" class="font-mono hover:underline">{{ formatNumber(row.number) }}</ULink>
                </template>
                <template #patient-data="{ row }">
                    <div class="leading-5">
                        <p v-if="patientName(row.id)">{{ patientName(row.id) }}</p>
                        <p v-else class="text-gray-300">–</p>
                        <span v-if="row.area" class="text-sm">{{ row.area }}</span>
                    </div>
                </template>
                <template #reason-data="{ row }">
                    <span v-if="row.reason">{{ row.reason.label }}</span>
                    <span v-else class="text-gray-300">–</span>
                </template>
                <template #arrived-data="{ row }">
                    <div class="leading-5">
                        <p>{{ formatArrived(row.arrived_at, todayIso(now)) }}</p>
                        <span class="text-sm">{{ elapsedMinutes(row.arrived_at, now) }} min ago</span>
                    </div>
                </template>
                <template #status-data="{ row }">
                    <BadgeInterventionStatus :status="row.status" />
                </template>
            </UTable>
        </div>

        <div class="space-y-1">
            <div class="flex h-8 items-center justify-between gap-2">
                <h4 class="text-sm font-medium text-gray-600">Closed</h4>
                <UInput v-model="day" type="date" icon="i-ph-calendar-duotone" />
            </div>
            <UTable
                :rows="closed"
                :columns="columns"
                :loading="loading"
                :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                :empty-state="{ icon: 'i-ph-clipboard-text-duotone', label: 'No closed interventions on this day.' }"
                :ui="tableUi"
                @select="openIntervention"
            >
                <template #number-data="{ row }">
                    <ULink :to="interventionPath(row)" class="font-mono hover:underline">{{ formatNumber(row.number) }}</ULink>
                </template>
                <template #patient-data="{ row }">
                    <div class="leading-5">
                        <p v-if="patientName(row.id)">{{ patientName(row.id) }}</p>
                        <p v-else class="text-gray-300">–</p>
                        <span v-if="row.area" class="text-sm">{{ row.area }}</span>
                    </div>
                </template>
                <template #reason-data="{ row }">
                    <div class="leading-5">
                        <p v-if="row.reason">{{ row.reason.label }}</p>
                        <p v-else class="text-gray-300">–</p>
                        <span v-if="row.disposition" class="text-sm">{{ row.disposition.label }}</span>
                    </div>
                </template>
                <template #arrived-data="{ row }">
                    <div class="leading-5">
                        <p>{{ formatArrived(row.arrived_at, day) }}</p>
                        <span v-if="row.discharged_at" class="text-sm">Stayed {{ durationMinutes(row) }} min</span>
                    </div>
                </template>
                <template #status-data="{ row }">
                    <BadgeInterventionStatus :status="row.status" />
                </template>
            </UTable>
        </div>
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { InterventionStatus, type FieldPost, type FieldPostPatient, type Intervention } from '~/types/fieldPost'
import { elapsedMinutes, todayIso } from '~/utils/fieldPosts'

const props = defineProps<{ post: FieldPost }>()

const client = useSupabaseClient()

const loading = ref(true)
const open = ref<Intervention[]>([])
const closed = ref<Intervention[]>([])
const patients = ref<Record<string, FieldPostPatient>>({})
const day = ref(todayIso())
const now = ref(new Date())
let ticker: ReturnType<typeof setInterval> | undefined

const SELECT = 'id, number, post_id, area, status, arrived_at, discharged_at, reason:options!interventions_reason_id_fkey (id, label), disposition:options!interventions_disposition_id_fkey (id, label)'

const columns = [
    { key: 'number', label: '#', class: 'w-px' },
    { key: 'patient', label: 'Patient' },
    { key: 'reason', label: 'Reason', class: 'w-full' },
    { key: 'arrived', label: 'Arrived' },
    { key: 'status', label: 'Status', class: 'w-px' },
]

const tableUi = { td: { padding: 'py-3' }, tr: { base: 'cursor-pointer hover:bg-gray-50' } }

function formatNumber(n: number) {
    return String(n).padStart(6, '0')
}

function formatArrived(iso: string, referenceDay: string) {
    return dayjs(iso).format('YYYY-MM-DD') === referenceDay ? dayjs(iso).format('HH:mm') : dayjs(iso).format('MMM D, YYYY HH:mm')
}

function durationMinutes(i: Intervention) {
    return Math.max(0, dayjs(i.discharged_at).diff(dayjs(i.arrived_at), 'minute'))
}

function interventionPath(i: Intervention) {
    return `/field-posts/${props.post.id}/interventions/${i.id}`
}

function openIntervention(i: Intervention) {
    navigateTo(interventionPath(i))
}

function patientName(id: string) {
    const p = patients.value[id]
    return p ? [p.first_name, p.last_name].filter(Boolean).join(' ') : ''
}

onMounted(() => {
    fetchData()
    ticker = setInterval(() => { now.value = new Date() }, 30_000)
})

onUnmounted(() => clearInterval(ticker))

watch(day, (value) => {
    if (!value) {
        day.value = todayIso()
        return
    }
    fetchData()
})

let fetchSeq = 0

async function fetchData() {
    const seq = ++fetchSeq
    loading.value = true
    const start = dayjs(day.value).startOf('day').toISOString()
    const end = dayjs(day.value).endOf('day').toISOString()

    const [openRes, closedRes] = await Promise.all([
        client.from('interventions').select(SELECT).eq('post_id', props.post.id).eq('status', InterventionStatus.inProgress).order('arrived_at'),
        client.from('interventions').select(SELECT).eq('post_id', props.post.id).neq('status', InterventionStatus.inProgress)
            .gte('arrived_at', start).lte('arrived_at', end).order('arrived_at', { ascending: false }),
    ])
    if (seq !== fetchSeq) return
    open.value = (openRes.data ?? []) as unknown as Intervention[]
    closed.value = (closedRes.data ?? []) as unknown as Intervention[]

    const ids = [...open.value, ...closed.value].map(i => i.id)
    let found: FieldPostPatient[] = []
    if (ids.length) {
        const { data } = await client.rpc('get_intervention_patients', { p_intervention_ids: ids } as never)
        found = (data ?? []) as FieldPostPatient[]
    }
    if (seq !== fetchSeq) return
    patients.value = Object.fromEntries(found.map(p => [p.intervention_id!, p]))
    loading.value = false
}
</script>
